import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Shield, Clock, CheckCircle, AlertCircle, XCircle, Eye, RefreshCw,
  Search, Filter, ChevronDown, ChevronUp, CreditCard, MapPin, Phone,
  Mail, Calendar, FileText, User, X
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
  approved: { label: 'Aprovado', color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  rejected: { label: 'Rejeitado', color: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  ready: { label: 'Pronto', color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  completed: { label: 'Concluído', color: 'bg-gray-100 text-gray-700', dot: 'bg-gray-500' },
};

const PASSPORT_STATUS_LABELS = {
  perdido: 'Perdido',
  roubado: 'Roubado',
  expirado: 'Expirado',
  danificado: 'Danificado',
};

const PAYMENT_LABELS = {
  pending: 'Aguarda Pagamento',
  paid: 'Pago',
};

export default function SCondutManagement() {
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let result = [...requests];
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(r =>
        (r.full_name || '').toLowerCase().includes(q) ||
        (r.reference_code || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.phone || '').includes(q)
      );
    }
    setFiltered(result);
  }, [requests, statusFilter, search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/salvo-conduto');
      setRequests(res.data || []);
    } catch (err) {
      toast.error('Erro ao carregar pedidos de Salvo-Conduto');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus, notes) => {
    setUpdatingStatus(true);
    try {
      await api.put(`/salvo-conduto/${id}/status`, { status: newStatus, notes: notes || null });
      toast.success(`Estado atualizado para: ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchData();
      if (showDetail && selectedReq?.id === id) {
        setSelectedReq(prev => ({ ...prev, status: newStatus }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar estado');
    } finally {
      setUpdatingStatus(false);
      setShowRejectModal(false);
      setRejectReason('');
      setRejectingId(null);
    }
  };

  const openRejectModal = (id) => {
    setRejectingId(id);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const openDetail = (req) => {
    setSelectedReq(req);
    setShowDetail(true);
  };

  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    completed: requests.filter(r => r.status === 'completed' || r.status === 'ready').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scondut-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salvo-Condutos</h1>
          <p className="text-sm text-gray-500 mt-1">Gestão de pedidos de Salvo-Conduto</p>
        </div>
        <button onClick={fetchData} className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="refresh-btn">
          <RefreshCw size={18} className="text-gray-600" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Total', value: stats.total, color: 'bg-gray-900', textColor: 'text-white' },
          { label: 'Pendentes', value: stats.pending, color: 'bg-yellow-50 border border-yellow-200', textColor: 'text-yellow-700' },
          { label: 'Aprovados', value: stats.approved, color: 'bg-emerald-50 border border-emerald-200', textColor: 'text-emerald-700' },
          { label: 'Rejeitados', value: stats.rejected, color: 'bg-red-50 border border-red-200', textColor: 'text-red-700' },
          { label: 'Concluídos', value: stats.completed, color: 'bg-blue-50 border border-blue-200', textColor: 'text-blue-700' },
        ].map((stat, idx) => (
          <div key={idx} className={`${stat.color} rounded-xl p-4 text-center`} data-testid={`stat-${stat.label.toLowerCase()}`}>
            <p className={`text-2xl font-bold ${stat.textColor}`}>{stat.value}</p>
            <p className={`text-xs mt-1 ${stat.textColor} opacity-70`}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar por nome, referência, email..."
            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1 flex-wrap">
          {[
            { id: 'all', label: 'Todos' },
            { id: 'pending', label: 'Pendentes' },
            { id: 'approved', label: 'Aprovados' },
            { id: 'rejected', label: 'Rejeitados' },
            { id: 'ready', label: 'Prontos' },
            { id: 'completed', label: 'Concluídos' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setStatusFilter(f.id)}
              data-testid={`filter-${f.id}`}
              className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                statusFilter === f.id
                  ? 'bg-[#0A0A0A] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="scondut-table">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Referência</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Requerente</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Destino</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Motivo</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Pagamento</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Estado</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Data</th>
                <th className="text-center text-xs font-medium text-gray-500 uppercase px-4 py-3">Acções</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((req) => {
                const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                return (
                  <tr key={req.id} className="hover:bg-gray-50" data-testid={`row-${req.id}`}>
                    <td className="px-4 py-3">
                      <span className="text-sm font-mono font-medium text-[#CC0000]">{req.reference_code}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{req.full_name}</p>
                        <p className="text-xs text-gray-500">{req.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{req.destination_country}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{PASSPORT_STATUS_LABELS[req.passport_status] || req.passport_status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        req.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {PAYMENT_LABELS[req.payment_status] || req.payment_status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-full w-fit ${sc.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{new Date(req.created_at).toLocaleDateString('pt-PT')}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openDetail(req)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Ver detalhes"
                          data-testid={`view-${req.id}`}
                        >
                          <Eye size={16} />
                        </button>
                        {req.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateStatus(req.id, 'approved')}
                              disabled={updatingStatus}
                              className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-50"
                              title="Aprovar"
                              data-testid={`approve-${req.id}`}
                            >
                              <CheckCircle size={16} />
                            </button>
                            <button
                              onClick={() => openRejectModal(req.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              title="Rejeitar"
                              data-testid={`reject-${req.id}`}
                            >
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        {req.status === 'approved' && (
                          <button
                            onClick={() => updateStatus(req.id, 'ready')}
                            disabled={updatingStatus}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50"
                            title="Marcar como Pronto"
                            data-testid={`ready-${req.id}`}
                          >
                            <FileText size={16} />
                          </button>
                        )}
                        {req.status === 'ready' && (
                          <button
                            onClick={() => updateStatus(req.id, 'completed')}
                            disabled={updatingStatus}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                            title="Marcar como Concluído"
                            data-testid={`complete-${req.id}`}
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                    <Shield size={40} className="mx-auto mb-3 opacity-30" />
                    <p>Nenhum pedido de Salvo-Conduto encontrado</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="reject-modal">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Rejeitar Pedido</h3>
              <button onClick={() => setShowRejectModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={18} />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Rejeição</label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Indique o motivo da rejeição..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
                data-testid="reject-reason-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={() => updateStatus(rejectingId, 'rejected', rejectReason)}
                disabled={!rejectReason.trim() || updatingStatus}
                className="px-4 py-2 text-sm text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                data-testid="confirm-reject-btn"
              >
                {updatingStatus ? 'A rejeitar...' : 'Confirmar Rejeição'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedReq && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="detail-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-5 border-b border-gray-200 flex items-center justify-between z-10">
              <div>
                <h3 className="font-bold text-gray-900 text-lg">Detalhes do Salvo-Conduto</h3>
                <p className="text-sm text-[#CC0000] font-mono">{selectedReq.reference_code}</p>
              </div>
              <button onClick={() => setShowDetail(false)} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-detail-btn">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Status badge */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${(STATUS_CONFIG[selectedReq.status] || STATUS_CONFIG.pending).color}`}>
                  {(STATUS_CONFIG[selectedReq.status] || STATUS_CONFIG.pending).label}
                </span>
                <span className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                  selectedReq.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {PAYMENT_LABELS[selectedReq.payment_status] || selectedReq.payment_status}
                </span>
              </div>

              {/* Personal info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoField icon={User} label="Nome Completo" value={selectedReq.full_name} />
                <InfoField icon={Mail} label="Email" value={selectedReq.email} />
                <InfoField icon={Phone} label="Telefone" value={selectedReq.phone} />
                <InfoField icon={Calendar} label="Data de Nascimento" value={selectedReq.birth_date} />
                <InfoField icon={MapPin} label="Naturalidade" value={selectedReq.birth_place} />
                <InfoField icon={Shield} label="Nacionalidade" value={selectedReq.nationality} />
              </div>

              {/* Passport info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Informação do Passaporte</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <InfoField label="N.º Passaporte Anterior" value={selectedReq.last_passport_number || 'N/D'} />
                  <InfoField label="Estado do Passaporte" value={PASSPORT_STATUS_LABELS[selectedReq.passport_status] || selectedReq.passport_status} />
                  {selectedReq.police_report_number && (
                    <InfoField label="N.º Queixa Policial" value={selectedReq.police_report_number} />
                  )}
                </div>
              </div>

              {/* Address */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Morada em Espanha</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <InfoField label="Endereço" value={selectedReq.address_spain} />
                  <InfoField label="Cidade" value={selectedReq.city} />
                  {selectedReq.postal_code && <InfoField label="Código Postal" value={selectedReq.postal_code} />}
                </div>
              </div>

              {/* Filiation */}
              {(selectedReq.father_name || selectedReq.mother_name) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Filiação</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedReq.father_name && <InfoField label="Pai" value={selectedReq.father_name} />}
                    {selectedReq.mother_name && <InfoField label="Mãe" value={selectedReq.mother_name} />}
                  </div>
                </div>
              )}

              {/* Travel */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Informação de Viagem</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <InfoField label="País de Destino" value={selectedReq.destination_country} />
                  {selectedReq.departure_date && <InfoField label="Data de Partida" value={selectedReq.departure_date} />}
                  <InfoField label="Motivo" value={selectedReq.reason} />
                </div>
              </div>

              {/* Notes */}
              {selectedReq.notes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-amber-800 mb-1">Observações do Requerente</h4>
                  <p className="text-sm text-amber-700">{selectedReq.notes}</p>
                </div>
              )}

              {selectedReq.admin_notes && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-800 mb-1">Notas Administrativas</h4>
                  <p className="text-sm text-blue-700">{selectedReq.admin_notes}</p>
                </div>
              )}

              {/* Fee */}
              <div className="flex items-center justify-between p-4 bg-gray-900 text-white rounded-lg">
                <div className="flex items-center gap-2">
                  <CreditCard size={18} />
                  <span className="text-sm font-medium">Taxa</span>
                </div>
                <span className="text-lg font-bold">EUR {(selectedReq.fee || 10).toFixed(2)}</span>
              </div>

              {/* Actions */}
              {selectedReq.status === 'pending' && (
                <div className="flex gap-2 justify-end pt-2">
                  <button
                    onClick={() => { setShowDetail(false); openRejectModal(selectedReq.id); }}
                    className="px-4 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg font-medium"
                  >
                    Rejeitar
                  </button>
                  <button
                    onClick={() => { updateStatus(selectedReq.id, 'approved'); setShowDetail(false); }}
                    disabled={updatingStatus}
                    className="px-5 py-2 text-sm text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg font-medium disabled:opacity-50"
                  >
                    Aprovar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      {Icon && <Icon size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />}
      <div>
        <p className="text-[11px] text-gray-500 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-gray-900">{value || 'N/D'}</p>
      </div>
    </div>
  );
}
